import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { roles } from 'common/constants/options';
import { getTourSteps, roleGroup } from 'common/constants/tourSteps';
import SidebarDrawerContext from 'context/SidebarDrawerContext';
import { hasMenuAnchoredSteps, isTourTargetVisible, visibleTourSteps } from 'components/shared/tour/tourDom';
import routes from 'common/routes';

// Evento global para relanzar el tour desde el menú ("Ver tutorial guiado").
export const TOUR_REPLAY_EVENT = 'otx:start-tour';
// Clave por usuario: cada usuario nuevo ve el tour una vez (en dispositivos compartidos no se
// "hereda" el de otro). Solo se marca como visto cuando lo SALTAN o lo COMPLETAN.
const doneKey = (group, userId) => `otx_tour_done_${group}_${userId || 'anon'}`;

// Quita el resaltado dorado de cualquier ítem del tour.
const clearTourHighlight = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.otx-tour-highlight').forEach((el) => el.classList.remove('otx-tour-highlight'));
};

// Cada cuánto se comprueba que el ancla del paso en curso sigue a la vista. Joyride NO avisa
// cuando el DOM cambia por su cuenta (rotar la pantalla, filtrar el buscador del menú, cerrar el
// panel): solo emite eventos cuando cambia SU estado interno. Por eso vigilamos nosotros.
const WATCHDOG_MS = 400;

// Cliente, entrenador y supervisor tienen tour (admin no).
const hasTour = (role) => role === roles.client || role === roles.trainer || role === roles.supervisor;

/**
 * Tour de onboarding guiado por rol (react-joyride). Resalta elementos reales del menú (spotlight)
 * y avanza al dar clic en "Siguiente". Se muestra UNA vez por grupo de rol (localStorage) y puede
 * relanzarse disparando `TOUR_REPLAY_EVENT`. Los pasos cuyo elemento no exista en el DOM (según
 * plataforma o en móvil) se omiten para no romper el recorrido.
 */
function RoleTour({ role = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation('tour');
  const [run, setRun] = useState(false);
  const userId = useSelector((state) => state.auth.user?.id);
  // El gestor de empresa es un CLIENT con flag: sin esto cae en el grupo 'client', cuyas anclas
  // no existen en su menú, y el tour queda en un cascarón de un paso.
  const isOrgManager = useSelector((state) => Boolean(state.auth.user?.is_org_manager));
  const { closeMobileSidebar, mobileOpen, openMobileSidebar } = useContext(SidebarDrawerContext);
  const { pathname } = useLocation();

  // Pantalla de inicio de cada rol: es el único sitio donde tiene sentido arrancar el tour solo.
  const isHomeRoute =
    pathname === routes.clientDashboard.path ||
    pathname === routes.trainerDashboard.path ||
    pathname === routes.adminDashboard.path ||
    pathname === routes.orgClientDashboard.path;

  const group = hasTour(role) ? roleGroup(role, isOrgManager) : null;
  const steps = useMemo(
    () => (group ? getTourSteps(role, t, isMobile, isOrgManager) : []),
    [group, role, t, isMobile, isOrgManager]
  );

  // Los pasos activos se CONGELAN al arrancar, en estado, y no se derivan en cada render.
  // Derivarlos era peligroso: si la lista encogía a mitad del recorrido, joyride recibía otro
  // array y su índice pasaba a apuntar a un paso distinto — el tour daba saltos solo. Además,
  // vaciarlos DESMONTA <Joyride>, y eso es una garantía mucho más dura que `run={false}`: sin
  // componente no hay portal, y sin portal no puede quedar ningún velo huérfano.
  const [activeSteps, setActiveSteps] = useState([]);
  // Espejo síncrono de `run`: los efectos de abajo lo leen antes de que llegue el re-render, que
  // es lo que evita el bucle entre "cerrar el panel" y "cortar el tour".
  const runRef = useRef(false);
  // Un único intento de arranque automático por sesión (ver el efecto de abajo).
  const autoStartAttempted = useRef(false);
  // Ancla del paso en curso, para el vigilante.
  const currentTargetRef = useRef(null);

  // Móvil: las anclas del menú viven dentro del panel deslizante. Se abre antes de correr para
  // que existan y se vean.
  const openSidebarIfMobile = useCallback(() => {
    if (isMobile) openMobileSidebar();
  }, [isMobile, openMobileSidebar]);

  /**
   * SALIDA ÚNICA. Todo lo que termina el tour pasa por aquí: limpia el resaltado, VACÍA los pasos
   * (con lo que <Joyride> se desmonta y no queda portal ni velo que puedan sobrevivir) y cierra el
   * panel. El cierre NO va condicionado a `isMobile` a propósito: justo en el caso peor (acaba de
   * cambiar el breakpoint por una rotación) ese guard dejaría el panel abierto con su propio velo,
   * que es del mismo tono y resulta indistinguible.
   */
  const stopTour = useCallback(
    (markSeen = false) => {
      runRef.current = false;
      currentTargetRef.current = null;
      clearTourHighlight();
      setRun(false);
      setActiveSteps([]);
      if (markSeen && group) localStorage.setItem(doneKey(group, userId), '1');
      closeMobileSidebar();
    },
    [group, userId, closeMobileSidebar]
  );

  /**
   * Enciende el tour SOLO si de verdad hay dónde apoyarlo.
   *
   * Antes se encendía a ciegas tras un temporizador. Si el panel no había abierto (o el usuario lo
   * cerró en ese hueco) el tour arrancaba igual y se quedaba el velo sin tooltip. Ahora se exige
   * que sobreviva al menos un paso anclado AL MENÚ: la bienvenida y el paso de la foto no cuentan,
   * porque existen siempre y darían un falso positivo.
   */
  const startTour = useCallback(() => {
    const usable = visibleTourSteps(steps);
    if (usable.length === 0 || !hasMenuAnchoredSteps(usable)) {
      // ESTE era el "menú pegado", y su bucle.
      //
      // Para poder medir las anclas, unas líneas más arriba se ABRE el panel. Si al llegar aquí no
      // hay ningún paso utilizable, antes se hacía `return` a secas y pasaban dos cosas: el panel
      // se quedaba abierto sin tooltip y sin nadie que lo cerrara, y como el tour nunca llegaba a
      // emitir su evento de inicio, TAMPOCO se marcaba como visto. Resultado: el intento se repetía
      // en cada carga y cada vez volvía a dejar el menú abierto. Eso explica por qué al pulsar "Ver
      // tutorial guiado" y luego "Saltar" el problema desaparecía: por esa vía el tour sí arranca,
      // sí se marca, y ya no vuelve a intentarlo solo.
      //
      // Se sale por `stopTour(true)`: cierra el panel Y lo da por visto. Un tour que no puede
      // pintarse no debe seguir secuestrando el menú en cada arranque; queda a un toque en "Ver
      // tutorial guiado", que no consulta esta marca.
      stopTour(true);
      return;
    }

    setActiveSteps(usable);
    runRef.current = true;
    setRun(true);
  }, [steps, stopTour]);

  // Arranca automáticamente la primera vez para este grupo de rol, y SOLO en su pantalla de
  // inicio. Antes dependía únicamente de [group, userId], así que se disparaba en cualquier
  // pantalla que montara el layout — incluida Términos y condiciones, que además es donde suele
  // aterrizar el `?redirect=` del login. Un tour del menú arrancando ahí no tiene sentido ni
  // aunque funcionara.
  useEffect(() => {
    if (!group || !isHomeRoute) return undefined;
    if (localStorage.getItem(doneKey(group, userId))) return undefined;
    // UN solo intento automático por sesión. El efecto depende de `isHomeRoute`, así que cada
    // regreso al inicio lo volvía a disparar y, con él, la apertura del panel para medir anclas:
    // quien no hubiera terminado el tutorial veía el menú abrirse solo una y otra vez. Quien lo
    // quiera de nuevo tiene el botón "Ver tutorial guiado", que no pasa por aquí.
    if (autoStartAttempted.current) return undefined;
    autoStartAttempted.current = true;

    openSidebarIfMobile();
    // Margen para que el panel esté montado y su animación haya terminado antes de MEDIR.
    const id = setTimeout(startTour, isMobile ? 900 : 600);
    // Si el componente se va antes de medir, el panel se quedaría abierto por nuestra culpa.
    return () => {
      clearTimeout(id);
      closeMobileSidebar();
    };
  }, [group, userId, isHomeRoute]);

  // Permite relanzarlo a demanda (botón "Ver tutorial guiado").
  useEffect(() => {
    // El temporizador se guarda para poder cancelarlo: suelto, si el usuario navegaba dentro de
    // esos 400 ms, encendía el tour en la pantalla NUEVA con las anclas de la vieja.
    let timer = null;
    const replay = () => {
      openSidebarIfMobile();
      timer = setTimeout(startTour, isMobile ? 400 : 0);
    };

    window.addEventListener(TOUR_REPLAY_EVENT, replay);
    return () => {
      window.removeEventListener(TOUR_REPLAY_EVENT, replay);
      if (timer) clearTimeout(timer);
    };
  }, [openSidebarIfMobile, isMobile, startTour]);

  const handleCallback = useCallback((data) => {
    const { action, status, lifecycle, step, type } = data;

    // SALIDA DE EMERGENCIA. Joyride pinta un velo negro al 60% SIN spotlight (lo ocultamos para
    // resaltar con clase propia), así que un tour que no puede pintar su tooltip deja la pantalla
    // en negro y sin nada que tocar. Antes solo se paraba al COMPLETARLO o SALTARLO: si el ancla
    // desaparecía —el drawer se cerró, se navegó a otra ruta— no había forma de salir.
    // Cerrar de verdad. `close` de joyride NO termina el tour: en modo continuo solo AVANZA un
    // paso, así que tocar la X (o el velo) repintaba lo mismo un paso más adelante y con ~20
    // pasos el usuario tocaba veinte veces creyendo que la pantalla estaba muerta. Esta rama es
    // la que le devuelve el control a quien ya está atrapado.
    if (action === ACTIONS.CLOSE) {
      stopTour(true);
      return;
    }

    if (status === STATUS.ERROR || type === EVENTS.TARGET_NOT_FOUND) {
      stopTour();
      return;
    }

    // Resalta el ítem REAL con una clase propia (no dependemos del spotlight de joyride, que
    // calcula mal el rect sobre la barra lateral). Se limpia y se vuelve a poner en cada paso.
    if (lifecycle === 'tooltip') {
      clearTourHighlight();
      // El vigilante necesita saber qué ancla mirar en cada momento.
      currentTargetRef.current = step?.target && step.target !== 'body' ? step.target : null;
      if (step?.target && step.target !== 'body') {
        const el = document.querySelector(step.target);
        if (el) {
          el.classList.add('otx-tour-highlight');
          // Desplazamiento CONTROLADO: movemos el contenedor con scroll del ítem resaltado (el
          // sidebar en desktop, la página "Más" en móvil), no Joyride por su cuenta — así el
          // tooltip de "Siguiente" nunca se aleja.
          // `block: 'center'` y NO 'nearest': 'nearest' es un no-op cuando el elemento ya se ve a
          // medias, así que los ítems del final del menú quedaban cortados ("mochos") y el dash
          // nunca bajaba. Centrarlo garantiza que se vea completo.
          el.scrollIntoView({ block: 'center', inline: 'nearest' });
        }
      }
    }

    // Solo se marca como visto si lo COMPLETAN o lo SALTAN explícitamente. Cualquier otra
    // interrupción no persiste, así el tour vuelve a aparecer hasta que el usuario lo cierre a propósito.
    // Se marca al ARRANCAR y no solo al terminar. Antes la clave solo se escribía si el usuario
    // lo completaba o lo saltaba, así que un tour interrumpido volvía a armarse en CADA carga:
    // eso es lo que convirtió un fallo puntual en "me pasa en todas las pantallas". Sigue estando
    // a un toque en "Ver tutorial guiado".
    if (type === EVENTS.TOUR_START && group) {
      localStorage.setItem(doneKey(group, userId), '1');
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour(true);
    }
  }, [group, userId, stopTour]);

  // VIGILANTE. Joyride pinta el velo mientras el tour corre, MIRE O NO si el ancla sigue en
  // pantalla, y solo emite eventos cuando cambia su propio estado: un cambio de DOM que no venga
  // de un clic (rotar, filtrar el buscador del menú, cerrar el panel) no dispara nada. Así que lo
  // comprobamos nosotros. Solo vive mientras el tour corre.
  useEffect(() => {
    if (!run) return undefined;

    const id = window.setInterval(() => {
      const target = currentTargetRef.current;
      if (target && !isTourTargetVisible(target)) stopTour();
    }, WATCHDOG_MS);

    return () => window.clearInterval(id);
  }, [run, stopTour]);

  // Si el usuario cierra el menú a mano mientras el tour corre (toca el scrim, la X, o navega
  // desde un ítem), sus anclas dejan de verse: se corta en vez de dejarlo apuntando al vacío.
  useEffect(() => {
    // `runRef` y no `run`: es síncrono, así que cuando `stopTour` cierra el panel y este efecto
    // se vuelve a disparar, ya está en false y no entra en bucle.
    if (isMobile && runRef.current && !mobileOpen) stopTour();
  }, [mobileOpen, isMobile, stopTour]);

  // Cruzar el breakpoint (rotar el teléfono) cambia el JUEGO DE ANCLAS entero: el layout monta el
  // sidebar de escritorio o el panel móvil, nunca los dos, y los pasos ni siquiera son los mismos.
  // Seguir sería incoherente.
  useEffect(() => {
    if (runRef.current) stopTour();
  }, [isMobile, stopTour]);

  // Cambiar de ruta mata el tour: sus anclas pertenecen a la pantalla que se acaba de dejar, y
  // seguir corriendo solo consigue dejar el velo puesto sobre una pantalla que ya no las tiene.
  // OJO: también hay que CERRAR el drawer. Antes solo se apagaba el tour, y como el drawer lo
  // había abierto el propio tour, quedaba huérfano: nadie más iba a cerrarlo y su backdrop (el
  // mismo tono que el velo) se quedaba encima capturando los toques.
  useEffect(() => {
    if (runRef.current) stopTour();
  }, [pathname]);

  // Limpia el resaltado si el componente se desmonta.
  useEffect(() => clearTourHighlight, []);

  if (!group || activeSteps.length === 0) return null;

  // `disableOverlayClose` vuelve a propósito. Se había quitado creyendo que tocar el velo daría
  // una salida, y resulta que con `continuous` ese toque NO cierra: llama a `close()`, que AVANZA
  // al paso siguiente. Con ~20 pasos harían falta ~20 toques a ciegas, que es justo la sensación
  // de "no se puede pulsar nada". La salida de verdad es el botón "Saltar" del tooltip, y ahora
  // el tooltip SIEMPRE se ve porque los pasos se filtran por visibilidad real.
  return (
    <Joyride
      disableOverlayClose
      steps={activeSteps}
      run={run}
      continuous
      showSkipButton
      showProgress
      // No dejamos que Joyride scrollee por su cuenta (scrolleaba el contenido de la vista y perdía el
      // tooltip). El reposicionamiento al cambiar de paso lo hacemos con scrollIntoView({block:'nearest'}).
      disableScrolling
      disableScrollParentFix
      callback={handleCallback}
      locale={{
        back: t('back'),
        last: t('last'),
        next: t('next'),
        skip: t('skip')
      }}
      styles={{
        options: {
          zIndex: 13000,
          primaryColor: theme.palette.gold.main,
          arrowColor: theme.palette.surface.content,
          backgroundColor: theme.palette.surface.content,
          textColor: theme.palette.text.primary,
          overlayColor: 'rgba(0, 0, 0, 0.6)'
        },
        spotlight: { display: 'none' },
        tooltip: { borderRadius: 16, border: `1px solid ${theme.palette.hairline.main}`, padding: 20 },
        tooltipTitle: { fontWeight: 800, fontSize: '1.05rem', marginBottom: 6 },
        tooltipContent: { fontSize: '0.92rem', lineHeight: 1.5, color: theme.palette.text.secondary },
        buttonNext: { backgroundColor: theme.palette.gold.main, color: '#0A0A0A', borderRadius: 10, fontWeight: 700, padding: '8px 16px' },
        buttonBack: { color: theme.palette.text.secondary, marginRight: 8 },
        buttonSkip: { color: theme.palette.text.secondary }
      }}
    />
  );
}

RoleTour.propTypes = {
  role: PropTypes.string
};
export default RoleTour;
